import uniq from "lodash-es/uniq";
import { Reducer, combineReducers } from "redux";

import {
    STROLLERS_LIST_FETCH_FAILURE,
    STROLLERS_LIST_FETCH_REQUEST,
    STROLLERS_LIST_FETCH_SUCCESS,
    StrollersFetchFailureAction,
    StrollersFetchRequestAction,
    StrollersFetchSuccessAction,
} from "@/mk2/apps/strollers/containers/BrandsStrollersList/BrandsStrollersList.actions";
import {
    initialBrandStrollersListState,
    initialBrandsStrollersListState,
} from "@/mk2/apps/strollers/containers/BrandsStrollersList/BrandsStrollersList.constants";
import { LinkedObjects } from "@/mk2/apps/strollers/containers/StrollersDetail/StrollersDetail.actions";
import { tupdate } from "@/mk2/helpers/types";
import { errorMessageFiltered } from "@/mk2/reducers/errorMessage";
import { LoadingState, loadingStateFiltered } from "@/mk2/reducers/loadingState";

/**
 * Defaults to true till the server don't say other response
 *
 * @param state
 * @param action
 * @returns {boolean}
 */
const hasMore: Reducer<boolean> = (state: boolean = true, action: StrollersFetchSuccessAction) => {
    switch (action.type) {
        case STROLLERS_LIST_FETCH_SUCCESS:
            return action.hasMore;
        default:
            return state;
    }
};

const count: Reducer<number> = (state: number = null, action: StrollersFetchSuccessAction) => {
    const firstFetch = !action.offset;
    switch (action.type) {
        case STROLLERS_LIST_FETCH_SUCCESS:
            return firstFetch ? action.count : state;
        default:
            return state;
    }
};

const brandId: Reducer<number> = (state: number = null, action: StrollersFetchSuccessAction) => {
    switch (action.type) {
        case STROLLERS_LIST_FETCH_SUCCESS:
            return action.response.result.brand;
        default:
            return state;
    }
};

export interface BrandStrollersListState {
    strollerIds: number[];
    strollersPreviewPhotoMap: Record<number, number>;
    strollersPhotosIds: number[];
    count: number;
    loadingState: LoadingState;
    errorMessage: string;
    hasMore: boolean;
    brandId: number;
    articleIds: number[];
}

const strollersListReducer: Reducer<BrandStrollersListState> =
    combineReducers<BrandStrollersListState>({
        loadingState: loadingStateFiltered("STROLLERS_LIST_FETCH"),
        errorMessage: errorMessageFiltered("STROLLERS_LIST_FETCH"),
        strollerIds: (state = [], action) => {
            switch (action.type) {
                case STROLLERS_LIST_FETCH_SUCCESS:
                    return uniq([...state, ...action.response.result.strollers]);
                default:
                    return state;
            }
        },
        strollersPreviewPhotoMap: (state = {}, action) => {
            switch (action.type) {
                case STROLLERS_LIST_FETCH_SUCCESS:
                    return tupdate(state, action.response.result.strollersPreviewPhotoMap);
                default:
                    return state;
            }
        },
        strollersPhotosIds: (state = [], action) => {
            switch (action.type) {
                case STROLLERS_LIST_FETCH_SUCCESS:
                    return uniq([...state, ...action.response.result.strollersPhotos]);
                default:
                    return state;
            }
        },
        count,
        hasMore,
        brandId,
        articleIds: (state = [], action) => {
            const firstFetch = !action.offset;
            switch (action.type) {
                case STROLLERS_LIST_FETCH_SUCCESS:
                    return firstFetch ? action.response.result.articles : state;
                default:
                    return state;
            }
        },
    });

export interface BrandsStrollersListState {
    buyingGuideId: number;
    counsellingId: number;
    forumPostLinkedObjects: LinkedObjects;
    brands: {
        [slug: string]: BrandStrollersListState;
    };
}

type StrollersFetchAction =
    | StrollersFetchRequestAction
    | StrollersFetchSuccessAction
    | StrollersFetchFailureAction;

export const strollersReducer: Reducer<BrandsStrollersListState> = (
    state: BrandsStrollersListState = initialBrandsStrollersListState,
    action: StrollersFetchAction,
) => {
    const slug = action.slug;

    switch (action.type) {
        case STROLLERS_LIST_FETCH_REQUEST:
        case STROLLERS_LIST_FETCH_FAILURE:
            return {
                ...state,
                brands: tupdate(state.brands, {
                    [slug]: strollersListReducer(
                        state.brands[slug] || initialBrandStrollersListState,
                        action,
                    ),
                }),
            };

        case STROLLERS_LIST_FETCH_SUCCESS:
            const firstFetch = !action.offset;
            return firstFetch
                ? {
                      ...state,
                      brands: tupdate(state.brands, {
                          [slug]: strollersListReducer(
                              state.brands[slug] || initialBrandStrollersListState,
                              action,
                          ),
                      }),
                      buyingGuideId: action.response.result.buyingGuide,
                      counsellingId: action.response.result.counselling,
                      forumPostLinkedObjects: action.forumPostLinkedObjects,
                  }
                : {
                      ...state,
                      brands: tupdate(state.brands, {
                          [slug]: strollersListReducer(
                              state.brands[slug] || initialBrandStrollersListState,
                              action,
                          ),
                      }),
                  };

        default:
            return state;
    }
};

export default strollersReducer;
